import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button, useToast } from "@mantis-sas/ui";
import { X, Loader2, Package, CheckCircle, Upload, DollarSign, Users, Search, File, ShoppingCart, Zap, FileText } from "lucide-react";
import { createQuotationsForSuppliers } from "../../../../../../ApiCalls/mantisFlowsManangment";
import { getMantisResponse } from "../../../../../../ApiCalls/mantisIA";

const QuotationModal = ({
  isOpen,
  onClose,
  suppliers = [],
  orderItems = [],
  currentUser,
  clientId,
  orderId,
  onQuotationCreated,
}) => {
  const { toast } = useToast();

  // Local state for the modal
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState("");
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [isAnalyzingDocument, setIsAnalyzingDocument] = useState(false);

  // Input values state - using a more stable approach
  const [inputValues, setInputValues] = useState({});

  // Ref to track focused input
  const focusedInputRef = useRef(null);

  // Filter suppliers based on search term
  const filteredSuppliers = useMemo(() => {
    return suppliers.filter(
      (supplier) =>
        supplier.name
          ?.toLowerCase()
          .includes(supplierSearchTerm.toLowerCase()) ||
        supplier.email
          ?.toLowerCase()
          .includes(supplierSearchTerm.toLowerCase()) ||
        supplier.contact_email
          ?.toLowerCase()
          .includes(supplierSearchTerm.toLowerCase())
    );
  }, [suppliers, supplierSearchTerm]);

  // Handle supplier selection
  const handleSupplierSelect = useCallback(
    (supplier) => {
      setSelectedSupplier(supplier);
      setShowSupplierDropdown(false);
      setSupplierSearchTerm(supplier.name);

      // Pre-fill input values with IPR data
      const newInputValues = {};
      if (orderItems && orderItems.length > 0) {
        orderItems.forEach((item) => {
          newInputValues[`${item.id}-price`] = item.estimated_price || 0;
          newInputValues[`${item.id}-currency`] = item.currency || "COP";
          newInputValues[`${item.id}-unit`] = item.unit || "";
          newInputValues[`${item.id}-quantity`] = item.quantity_to_order || 0;
        });
      }
      setInputValues(newInputValues);
    },
    [orderItems]
  );

  // Handle item selection
  const handleItemToggle = useCallback((item) => {
    setSelectedItems((prev) => {
      const isSelected = prev.some((i) => i.id === item.id);
      if (isSelected) {
        return prev.filter((i) => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  }, []);

  // Stable input change handler
  const handleInputChange = useCallback((itemId, field, value) => {
    setInputValues((prev) => ({
      ...prev,
      [`${itemId}-${field}`]: value,
    }));
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback((event) => {
    const files = Array.from(event.target.files);
    const newAttachments = files.map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
    }));
    setAttachments((prev) => [...prev, ...newAttachments]);
  }, []);

  // Remove attachment
  const removeAttachment = useCallback((attachmentId) => {
    setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
  }, []);

  // Helper function to format file size
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }, []);

  // Helper function to convert file to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1]; // Remove data URL prefix
        resolve({
          base64: base64,
          filename: file.name,
          mime_type: file.type,
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Function to analyze document and fill inputs automatically
  const analyzeDocumentAndFillInputs = async () => {
    if (attachments.length === 0) {
      toast({
        title: "Error",
        description: "Por favor sube un documento para analizar",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzingDocument(true);
    try {
      // Convert the first attachment to base64
      const fileData = await convertFileToBase64(attachments[0].file);

      // Prepare the prompt with context about available suppliers and items
      const prompt = generateAnalysisPrompt(suppliers, orderItems);

      // Call the AI service
      const response = await getMantisResponse(
        prompt,
        [fileData],
        "Eres un asistente especializado en análisis de documentos de cotización y propuestas comerciales. Tu tarea es extraer información precisa sobre precios, cantidades, proveedores y detalles de productos de documentos comerciales. Responde ÚNICAMENTE en formato JSON válido sin texto adicional."
      );

      // Parse the AI response
      const analysisResult = parseAIResponse(response.response);

      // Apply the analysis results to fill inputs
      applyAnalysisResults(analysisResult);

      toast({
        title: "Éxito",
        description: "Documento analizado y campos completados automáticamente",
      });
    } catch (error) {
      console.error("Error analyzing document:", error);
      toast({
        title: "Error",
        description:
          "Error al analizar el documento. Verifica que el archivo sea legible.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzingDocument(false);
    }
  };

  // Function to generate the analysis prompt
  const generateAnalysisPrompt = (availableSuppliers, availableItems) => {
    const suppliersInfo = availableSuppliers
      .map(
        (s) =>
          `- ${s.name} (ID: ${s.id}, Email: ${
            s.email || s.contact_email || "N/A"
          })`
      )
      .join("\n");

    const itemsInfo = availableItems
      .map(
        (item) =>
          `- ${item.name || item.item_description || `Item ${item.id}`} (ID: ${
            item.id
          }, SKU: ${item.item_sku || "N/A"}, Cantidad solicitada: ${
            item.quantity_to_order || 0
          })`
      )
      .join("\n");

    return `
Analiza el documento proporcionado y extrae la siguiente información en formato JSON:

PROVEEDORES DISPONIBLES EN LA PLATAFORMA:
${suppliersInfo}

ITEMS DISPONIBLES EN EL IPR:
${itemsInfo}

INSTRUCCIONES:
1. Identifica el proveedor mencionado en el documento y mapea con los proveedores disponibles
2. Extrae información de precios, cantidades y detalles de los items mencionados
3. Mapea los items del documento con los items disponibles en el IPR
4. Responde ÚNICAMENTE con un JSON válido con esta estructura exacta:

{
  "supplier_id": "ID del proveedor identificado",
  "supplier_name": "Nombre del proveedor",
  "items": [
    {
      "item_id": "ID del item en el IPR",
      "item_name": "Nombre del item",
      "unit_price": 0.00,
      "currency": "COP|USD|EUR",
      "quantity": 0,
      "unit": "unidad|kg|litro|etc"
    }
  ],
  "total_amount": 0.00,
  "currency": "COP|USD|EUR",
  "delivery_time_days": 0,
  "payment_terms": "términos de pago si están disponibles",
  "valid_until": "fecha de validez si está disponible"
}

IMPORTANTE:
- Si no encuentras información para algún campo, usa valores por defecto apropiados
- Para precios, usa solo números (sin símbolos de moneda)
- Para cantidades, usa solo números
- Mapea correctamente los items basándote en nombres similares o SKUs
- Si hay múltiples proveedores, selecciona el más relevante
- Responde SOLO con el JSON, sin texto adicional
`;
  };

  // Function to parse AI response
  const parseAIResponse = (response) => {
    try {
      // Clean the response to extract valid JSON
      let cleanedResponse = response;

      // Remove markdown formatting
      cleanedResponse = cleanedResponse
        .replace(/```json/g, "")
        .replace(/```/g, "");

      // Find JSON content
      const jsonStart = cleanedResponse.indexOf("{");
      const jsonEnd = cleanedResponse.lastIndexOf("}");

      if (jsonStart !== -1 && jsonEnd !== -1) {
        cleanedResponse = cleanedResponse.substring(jsonStart, jsonEnd + 1);
      }

      return JSON.parse(cleanedResponse);
    } catch (error) {
      console.error("Error parsing AI response:", error);
      throw new Error("Respuesta de IA no válida");
    }
  };

  // Function to apply analysis results to inputs
  const applyAnalysisResults = (analysisResult) => {
    // Set supplier if found
    if (analysisResult.supplier_id) {
      const supplier = suppliers.find(
        (s) => s.id.toString() === analysisResult.supplier_id.toString()
      );
      if (supplier) {
        handleSupplierSelect(supplier);
      }
    }

    // Set items and their values
    if (analysisResult.items && Array.isArray(analysisResult.items)) {
      const newInputValues = { ...inputValues };
      const newSelectedItems = [];

      analysisResult.items.forEach((analyzedItem) => {
        // Find matching item in orderItems
        const matchingItem = orderItems.find(
          (item) =>
            item.id.toString() === analyzedItem.item_id.toString() ||
            item.name
              ?.toLowerCase()
              .includes(analyzedItem.item_name?.toLowerCase()) ||
            item.item_sku === analyzedItem.item_sku
        );

        if (matchingItem) {
          newSelectedItems.push(matchingItem);

          // Set input values
          newInputValues[`${matchingItem.id}-price`] =
            analyzedItem.unit_price || 0;
          newInputValues[`${matchingItem.id}-currency`] =
            analyzedItem.currency || "COP";
          newInputValues[`${matchingItem.id}-unit`] = analyzedItem.unit || "";
          newInputValues[`${matchingItem.id}-quantity`] =
            analyzedItem.quantity || matchingItem.quantity_to_order || 0;
        }
      });

      setSelectedItems(newSelectedItems);
      setInputValues(newInputValues);
    }
  };

  // Create quotation
  const createQuotation = async () => {
    if (!selectedSupplier || selectedItems.length === 0) {
      toast({
        title: "Error",
        description: "Please select a supplier and at least one item",
        variant: "destructive",
      });
      return;
    }

    // Validate that all selected items have required data
    const missingData = selectedItems.filter((item) => {
      const price = inputValues[`${item.id}-price`];
      const quantity = inputValues[`${item.id}-quantity`];
      return (
        !price ||
        !quantity ||
        parseFloat(price) <= 0 ||
        parseFloat(quantity) <= 0
      );
    });

    if (missingData.length > 0) {
      toast({
        title: "Validation Error",
        description:
          "Please ensure all selected items have valid price and quantity values",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      // Prepare items data according to backend expectations
      const items = selectedItems.map((item) => ({
        id: item.id, // initial_purchase_request_item_id
        product_id: item.product_id || null,
        unit_price: parseFloat(inputValues[`${item.id}-price`]) || 0,
        currency: inputValues[`${item.id}-currency`] || "COP",
        quantity:
          parseFloat(inputValues[`${item.id}-quantity`]) ||
          item.quantity_to_order ||
          0,
        delivery_time_days: item.delivery_time_days || 0,
        comments: item.comments || "",
        unit: item.unit || "",
      }));

      console.log("Prepared items data:", items);
      console.log("Selected supplier:", selectedSupplier);

      // Prepare file data if attachments exist
      let fileData = null;
      if (attachments.length > 0) {
        // For now, we'll use the first attachment
        // In a real implementation, you might want to handle multiple files
        const attachment = attachments[0];
        fileData = await convertFileToBase64(attachment.file);
      }

      console.log("File data:", fileData);

      // Call the API
      const response = await createQuotationsForSuppliers(
        clientId,
        orderId,
        [selectedSupplier.id], // supplierIds array
        [selectedSupplier], // suppliers array
        items,
        fileData
      );

      console.log("Quotation creation response:", response);

      if (!response.success) {
        throw new Error(response.message || "Failed to create quotation");
      }

      toast({
        title: "Success",
        description: `Quotation created successfully for ${selectedSupplier.name}`,
      });

      // Call the callback to notify parent component
      if (onQuotationCreated) {
        onQuotationCreated({
          supplierId: selectedSupplier.id,
          supplierName: selectedSupplier.name,
          items: items,
          response: response,
          quotationData: response.data,
        });
      }

      // Close modal and reset state
      handleClose();
    } catch (error) {
      console.error("Error creating quotation:", error);
      toast({
        title: "Error",
        description: `Failed to create quotation: ${
          error.response?.data?.message || error.message
        }`,
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Handle modal close
  const handleClose = useCallback(() => {
    setSelectedSupplier(null);
    setSelectedItems([]);
    setAttachments([]);
    setSupplierSearchTerm("");
    setShowSupplierDropdown(false);
    setInputValues({});
    onClose();
  }, [onClose]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSupplierDropdown && !event.target.closest(".supplier-dropdown")) {
        setShowSupplierDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSupplierDropdown]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedSupplier(null);
      setSelectedItems([]);
      setAttachments([]);
      setSupplierSearchTerm("");
      setShowSupplierDropdown(false);
      setInputValues({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-800 rounded flex items-center justify-center">
              <ShoppingCart className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Crear Cotización
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Complete la información para crear una nueva cotización
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isCreating}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Step 1: Upload Document */}
            <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center">
                <FileText className="h-4 w-4 mr-2 text-blue-500" />
                1. Subir Documento de Cotización
              </h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id="quotation-file-upload"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                  />
                  <label
                    htmlFor="quotation-file-upload"
                    className="cursor-pointer inline-flex items-center px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Seleccionar Archivos
                  </label>
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {attachments.length} archivo(s) seleccionado(s)
                  </span>
                </div>

                {/* Auto-fill button */}
                {attachments.length > 0 && (
                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={analyzeDocumentAndFillInputs}
                      disabled={isAnalyzingDocument}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {isAnalyzingDocument ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Analizando...
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4 mr-2" />
                          Completar Automáticamente
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {attachments.length > 0 && (
                  <div className="space-y-2">
                    {attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700"
                      >
                        <div className="flex items-center space-x-2">
                          <File className="h-4 w-4 text-slate-500" />
                          <span className="text-sm text-slate-900 dark:text-slate-100">
                            {attachment.name}
                          </span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            ({formatFileSize(attachment.size)})
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAttachment(attachment.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: Select Supplier */}
            <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center">
                <Users className="h-4 w-4 mr-2 text-blue-500" />
                2. Seleccionar Proveedor
              </h4>

              {/* Supplier Dropdown */}
              <div className="relative supplier-dropdown">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Buscar proveedor..."
                    value={supplierSearchTerm}
                    onChange={(e) => {
                      setSupplierSearchTerm(e.target.value);
                      setShowSupplierDropdown(true);
                    }}
                    onFocus={() => setShowSupplierDropdown(true)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 pr-10"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <Search className="h-4 w-4 text-slate-400" />
                  </div>
                </div>

                {/* Dropdown */}
                {showSupplierDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredSuppliers.length > 0 ? (
                      filteredSuppliers.map((supplier) => (
                        <div
                          key={supplier.id}
                          className="px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer border-b border-slate-100 dark:border-slate-700 last:border-b-0"
                          onClick={() => handleSupplierSelect(supplier)}
                        >
                          <div className="font-medium text-slate-900 dark:text-slate-100">
                            {supplier.name}
                          </div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            {supplier.email || supplier.contact_email}
                          </div>
                          {supplier.phone && (
                            <div className="text-xs text-slate-400 dark:text-slate-500">
                              {supplier.phone}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-center text-slate-500 dark:text-slate-400">
                        {supplierSearchTerm
                          ? "No se encontraron proveedores"
                          : "No hay proveedores disponibles"}
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Supplier Display */}
                {selectedSupplier && (
                  <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-md">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-orange-900 dark:text-orange-100">
                          {selectedSupplier.name}
                        </div>
                        <div className="text-sm text-orange-700 dark:text-orange-300">
                          {selectedSupplier.email ||
                            selectedSupplier.contact_email}
                        </div>
                        {selectedSupplier.phone && (
                          <div className="text-xs text-orange-600 dark:text-orange-400">
                            {selectedSupplier.phone}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedSupplier(null);
                          setSupplierSearchTerm("");
                        }}
                        className="text-orange-600 hover:text-orange-800"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Step 3: Select Items */}
            {selectedSupplier && (
              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center">
                  <Package className="h-4 w-4 mr-2 text-green-500" />
                  3. Seleccionar Items del IPR
                </h4>
                {orderItems && orderItems.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {orderItems.map((item) => (
                      <div
                        key={item.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                          selectedItems.some((i) => i.id === item.id)
                            ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                            : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                        }`}
                        onClick={() => handleItemToggle(item)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-slate-900 dark:text-slate-100">
                              {item.name ||
                                item.item_description ||
                                `Item ${item.id}`}
                            </div>
                            <div className="text-sm text-slate-500 dark:text-slate-400">
                              Cantidad: {item.quantity_to_order || 0}{" "}
                              {item.unit || "unidades"}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {selectedItems.some((i) => i.id === item.id) && (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-500 dark:text-slate-400">
                      No hay items disponibles en el IPR
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Set Prices and Details */}
            {selectedItems.length > 0 && (
              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center">
                  <DollarSign className="h-4 w-4 mr-2 text-purple-500" />
                  4. Configurar Precios y Detalles
                </h4>
                <div className="space-y-4">
                  {selectedItems.map((item) => (
                    <div
                      key={`quotation-item-${item.id}`}
                      className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                    >
                      <div className="font-medium text-slate-900 dark:text-slate-100 mb-3">
                        {item.name ||
                          item.item_description ||
                          `Item ${item.id}`}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Precio Unitario
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={
                              inputValues[`${item.id}-price`] ||
                              item.estimated_price ||
                              0
                            }
                            onChange={(e) =>
                              handleInputChange(
                                item.id,
                                "price",
                                e.target.value
                              )
                            }
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Moneda
                          </label>
                          <select
                            value={
                              inputValues[`${item.id}-currency`] ||
                              item.currency ||
                              "COP"
                            }
                            onChange={(e) =>
                              handleInputChange(
                                item.id,
                                "currency",
                                e.target.value
                              )
                            }
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                          >
                            <option value="COP">COP</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Unidad
                          </label>
                          <input
                            type="text"
                            value={
                              inputValues[`${item.id}-unit`] || item.unit || ""
                            }
                            onChange={(e) =>
                              handleInputChange(item.id, "unit", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Cantidad
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={
                              inputValues[`${item.id}-quantity`] ||
                              item.quantity_to_order ||
                              0
                            }
                            onChange={(e) =>
                              handleInputChange(
                                item.id,
                                "quantity",
                                e.target.value
                              )
                            }
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
          <Button variant="outline" onClick={handleClose} disabled={isCreating}>
            Cancelar
          </Button>
          <Button
            onClick={createQuotation}
            disabled={
              !selectedSupplier || selectedItems.length === 0 || isCreating
            }
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creando...
              </>
            ) : (
              <>
                <ShoppingCart className="h-4 w-4 mr-2" />
                Crear Cotización
              </>
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuotationModal;
